/**
 * DOCUMENTATION:
 * Component: SystemDesignDashboard
 * Purpose: Central hub for the System Design & Scalability track.
 * * Note for Junior Developers:
 * - System Design is about trade-offs. There is no "perfect" system, only
 * the right system for a specific set of requirements.
 * - Scalability: The ability of a system to handle increased load by adding resources.
 * - This track covers the infrastructure that lives "behind" your React code.
 */

import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Box, Grid, Card, Button, Stack, styled, useTheme, useMediaQuery, TextField, InputAdornment, Fab, Zoom,
} from "@mui/material";

/* Icons */
import StorageIcon from '@mui/icons-material/Storage';
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloudQueueIcon from '@mui/icons-material/CloudQueue';

/* Project-specific components */
import Typography from "../../../components/Typography";
import LanguageTabs from "../../../components/LanguageTabs";
import LectureTimer from "../../../components/LectureTimer";
import RadioOption from "../../../components/RadioOption";

const bgImage = "https://images.unsplash.com/photo-1451187580459-43490279c0fa";

/* Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(10, 5, 25, 0.94), rgba(10, 5, 25, 0.96)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
    color: "white",
}));

const GlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(20, 15, 45, 0.8)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    height: "100%",
}));

const TopicBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    borderRadius: "10px",
    padding: theme.spacing(1.5, 2),
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(0, 200, 255, 0.08)",
        borderColor: "#00c8ff",
    },
}));

const ViewButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#00c8ff",
    color: "#0a0519",
    fontWeight: 700,
    borderRadius: "999px",
    padding: "6px 20px",
    textTransform: "none",
    "&:hover": {
        backgroundColor: "#00a3d1",
        transform: "scale(1.05)",
    },
}));

export default function SystemDesignDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [searchQuery, setSearchQuery] = useState("");

    const lessons = useMemo(() => [
        { title: "Scalability: Vertical vs Horizontal", link: "/system-design-fundamentals" },
        { title: "Load Balancing & High Availability", link: "/system-design-load-balancing" },
        { title: "Caching Strategies (Redis & Memcached)", link: "/system-design-caching" },
        { title: "Database Sharding & Replication", link: "/system-design-databases" },
        { title: "Microservices & API Gateways", link: "/system-design-microservices" },
        { title: "The CAP Theorem & Data Consistency", link: "/system-design-cap" },
        { title: "Message Queues & Event-Driven Dev", link: "/system-design-queues" },
        { title: "CDNs & Global Edge Computing", link: "/system-design-cdn" },
        { title: "System Design Practical: URL Shortener", link: "/system-design-project" },
        { title: "System Design Mastery Quiz", link: "/system-design-quiz" },
    ], []);

    const filteredLessons = useMemo(() => {
        const safeQuery = (searchQuery || "").toLowerCase();
        return lessons.filter(lesson =>
            (lesson?.title || "").toLowerCase().includes(safeQuery)
        );
    }, [searchQuery, lessons]);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 4, mt: 4, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "#00c8ff",
                        px: { xs: 3, md: 6 },
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                        textShadow: "0 0 15px rgba(0, 200, 255, 0.5)",
                    }}
                >
                    SYSTEM DESIGN & SCALABILITY
                </Typography>
            </Box>

            <LanguageTabs active="System Design" />

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 }, mt: 2 }}>
                <Grid item xs={12} md={5}>
                    <GlassCard>
                        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: "center", mb: 2, color: "#00c8ff" }}>
                            THE ARCHITECT'S VISION
                        </Typography>
                        <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.02)", borderRadius: "12px", textAlign: "center" }}>
                            <CloudQueueIcon sx={{ fontSize: 60, color: "#00c8ff", mb: 2 }} />
                            <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.8, opacity: 0.8 }}>
                                "Scalability is not just about adding more servers. It's about designing a system that stays responsive as its complexity and user base grows exponentially."
                            </Typography>
                        </Box>
                        <Box sx={{ mt: 3 }}>
                            <LectureTimer />
                        </Box>
                    </GlassCard>
                </Grid>

                <Grid item xs={12} md={7}>
                    <GlassCard>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search architecture concepts..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "#00c8ff" }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: "white",
                                    bgcolor: "rgba(255,255,255,0.05)",
                                    borderRadius: "8px",
                                    "& fieldset": { borderColor: "rgba(0, 200, 255, 0.3)" }
                                }
                            }}
                        />

                        <Stack spacing={1.5}>
                            {filteredLessons.map((lesson, i) => (
                                <TopicBox key={i}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <RadioOption />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {lesson.title}
                                        </Typography>
                                    </Box>
                                    <Link to={lesson.link} style={{ textDecoration: "none" }}>
                                        <ViewButton size="small">Architect</ViewButton>
                                    </Link>
                                </TopicBox>
                            ))}
                        </Stack>
                    </GlassCard>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
